import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import ContactSchemaData from '../data/seo/Contact';
import toast from 'react-hot-toast';

interface FormData {
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
}

const Contact: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            // Removed GET request warm-up

            // Prepare FormData
            const formPayload = new FormData();
            formPayload.append('name', formData.name);
            formPayload.append('email', formData.email);
            formPayload.append('phone', formData.phone);
            formPayload.append('subject', formData.subject);
            formPayload.append('message', formData.message);

            // Send POST request
            const response = await fetch('https://masahillshealthservices.com/api/c-masa.php', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    // Do NOT set Content-Type here!
                },
                body: formPayload,
            });

            const result = await response.json();

            if (response.ok && result.success) {
                toast.success('Your message has been sent successfully!', {
                    duration: 4000,
                    position: 'top-center',
                    style: {
                        background: '#10B981',
                        color: '#fff',
                        borderRadius: '10px',
                        padding: '16px',
                    },
                });
                setSubmitStatus('success');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                });
            } else {
                throw new Error(result.error || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            setSubmitStatus('error');
            toast.error('There was an error sending your message. Please try again later.', {
                duration: 4000,
                position: 'top-center',
                style: {
                    background: '#EF4444',
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '16px',
                },
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            {/* SEO Metadata */}
            <SEO
                title={ContactSchemaData.pageTitle}
                description={ContactSchemaData.pageDescription}
                keywords={ContactSchemaData.pageKeywords}
                canonical={ContactSchemaData.canonicalUrl}
                schema={ContactSchemaData.contactSchemaJson}
                openGraph={{
                    title: ContactSchemaData.pageTitle,
                    description: ContactSchemaData.pageDescription,
                    url: ContactSchemaData.canonicalUrl,
                    type: 'website',
                    image: '/images/logo.png',
                }}
            />
            {/* Hero Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-16"
                >
                    <div className="text-sm font-medium text-brand-primary mb-4">
                        <span className="inline-block px-3 py-1 bg-gray-50 rounded-full border border-gray-100">
                            Contact Us
                        </span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-brand-primary mb-6">
                        Get in <span className="text-brand-coral">Touch</span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        Have questions about our home care services? We'd love to hear from you.
                        Send us a message and we'll respond as soon as possible.
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-3 gap-12">
                    {/* Contact Information */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="lg:col-span-1"
                    >
                        <div className="bg-gradient-to-br from-brand-primary to-brand-teal p-8 rounded-xl text-black">
                            <h2 className="text-2xl font-bold mb-8">Contact Information</h2>

                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <div className="w-10 h-10 bg-brand-coral rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        📍
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Address</h3>
                                        <p className="text-black /90">
                                            3410 Texas Dr<br />
                                            Sachse, TX 75048
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-10 h-10 bg-brand-yellow rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        📞
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Phone</h3>
                                        <p className="text-black /90">
                                            <a href="tel:+1234567890" className="hover:text-brand-black transition-colors">
                                                (469) 573-0176
                                            </a>
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-10 h-10 bg-brand-magenta rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        ✉️
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Email</h3>
                                        <p className="text-black /90">
                                            <a href="mailto:contact@masahillshealthservices.com" className="hover:text-brand-yellow transition-colors">
                                                contact@masahillshealthservices.com
                                            </a>
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-10 h-10 bg-brand-blue rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        🕒
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Hours</h3>
                                        <p className="text-black /90">
                                            Mon-Fri: 8:00 AM - 6:00 PM<br />
                                            Sat: 9:00 AM - 4:00 PM<br />
                                            Sun: Emergency Only
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Contact Form */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="lg:col-span-2"
                    >
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
                            <h2 className="text-2xl font-bold text-brand-primary mb-6">Send us a Message</h2>

                            {submitStatus === 'success' && (
                                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center">
                                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm mr-3">
                                            ✓
                                        </div>
                                        <p className="text-green-700 font-medium">
                                            Thank you! Your message has been sent successfully. We'll get back to you soon.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {submitStatus === 'error' && (
                                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-center">
                                        <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-sm mr-3">
                                            ✕
                                        </div>
                                        <p className="text-red-700 font-medium">
                                            Sorry, there was an error sending your message. Please try again or call us directly.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-brand-primary mb-2" htmlFor="name">
                                            Full Name *
                                        </label>
                                        <input
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-brand-primary mb-2" htmlFor="email">
                                            Email Address *
                                        </label>
                                        <input
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-brand-primary mb-2" htmlFor="phone">
                                            Phone Number
                                        </label>
                                        <input
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-brand-primary mb-2" htmlFor="subject">
                                            Subject *
                                        </label>
                                        <select
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                                            id="subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleInputChange}
                                            required
                                            disabled={isSubmitting}
                                        >
                                            <option value="">Select a subject</option>
                                            <option value="General Inquiry">General Inquiry</option>
                                            <option value="Appointment Request">Appointment Request</option>
                                            <option value="Service Information">Service Information</option>
                                            <option value="Insurance Questions">Insurance Questions</option>
                                            <option value="Billing Support">Billing Support</option>
                                            <option value="Emergency">Emergency</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-brand-primary mb-2" htmlFor="message">
                                        Message *
                                    </label>
                                    <textarea
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                                        id="message"
                                        name="message"
                                        rows={5}
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        placeholder="Please describe how we can help you..."
                                        required
                                        disabled={isSubmitting}
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-brand-coral text-white font-semibold py-4 px-6 rounded-lg hover:bg-brand-coral/90 transition-all duration-300 focus:ring-2 focus:ring-brand-coral focus:ring-offset-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Sending Message...
                                        </span>
                                    ) : (
                                        'Send Message'
                                    )}
                                </button>
                            </form>
                        </div>
                    </motion.div>
                </div>

                {/* Emergency Contact Banner */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="mt-16 bg-gradient-to-r from-red-500 to-red-600 p-6 rounded-xl text-white text-center"
                >
                    <h3 className="text-xl font-bold mb-2">Medical Emergency?</h3>
                    <p className="mb-4">For immediate medical assistance, please call 911 or visit your nearest emergency room.</p>
                    <a
                        href="tel:911"
                        className="inline-block bg-white text-red-600 font-bold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Call 911 Now
                    </a>
                </motion.div>
            </div>
        </div>
    );
};

export default Contact;